/**
 * Sign-in.
 *
 * Collapses three legacy entry points into one form. `handleDoctorLogin`,
 * `handlePatientAuth` and `handleAdminLogin` all called the same
 * POST /api/auth/login and differed only in which modal they lived in and which
 * roles they then rejected — so a doctor typing into the patient box was told
 * "invalid permission" despite valid credentials. One form, and the role in the
 * response decides where the user lands.
 *
 * المرحلة 1.1 (F01): one box takes a username, an email or a mobile number —
 * which of them the platform accepts is its `signInMethods` policy, read
 * before sign-in from GET /api/auth/policy, and the server checks it again
 * per account. «نسيت كلمة السر؟» opens the code-by-email-or-SMS flow, and the
 * legal texts in force are named under the button.
 */

import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Field, Icon, Input, Panel } from '../../components/common';
import { ROLE_HOME, useAuth } from '../../context/AuthContext';
import { useGoogleSignIn, GOOGLE_ROLES } from '../../hooks/useGoogleSignIn';
import { identifierCopy } from '../../domain/identity';
import { AgencyMark, ProductMark } from '../../components/layout/BrandMarks';
import { useBranding } from '../../services/branding';
import { welcomeLine } from '../../domain/branding';
import { useI18n } from '../../i18n/I18nProvider';
import { applicantFrom, saveApplicant } from '../apply/applicant';
import { LanguageSwitch } from '../../i18n/LanguageSwitch';

const METHOD_ICONS = { username: 'user-round', email: 'mail', phone: 'smartphone' };

export function LoginPage() {
  const { login, loginWithGoogleResult, isAuthenticated, homePath } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useBranding();
  const { t, policy, country, lang } = useI18n();

  const [form, setForm] = useState({ identifier: '', password: '' });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleNotice, setGoogleNotice] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const redirectTo = location.state?.from || null;
  // A notice handed over by the reset screen or a session that ended.
  const notice = location.state?.notice || null;

  const google = useGoogleSignIn({
    onAuthenticated: (result) => {
      const session = loginWithGoogleResult(result);
      if (session) navigate(redirectTo || ROLE_HOME[session.role] || '/', { replace: true });
    },
    onError: (err) => {
      // المرحلة 1.3: a doctor waiting for approval goes to their application.
      const applicant = applicantFrom(err);
      if (applicant) {
        saveApplicant(applicant);
        navigate('/apply', { replace: true });
        return;
      }
      setServerError(err.message);
    },
  });

  if (isAuthenticated) return <Navigate to={redirectTo || homePath} replace />;

  const methods = policy?.signIn || ['username'];
  const copy = identifierCopy(methods, t, country?.mobile?.example || '01012345678');
  const consentTexts = policy?.consent?.required ? policy.consent.texts || [] : [];
  const titleOf = (text) => (lang === 'en' && text.titleEn ? text.titleEn : text.title);
  const resetOpen = policy ? policy.passwordReset !== 'admin' : true;

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setServerError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    // The same words under an empty field as before the phase.
    if (!form.identifier.trim()) nextErrors.identifier = t('auth.fieldRequired');
    if (!form.password) nextErrors.password = t('auth.fieldRequired');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    setServerError(null);
    try {
      const session = await login(form.identifier.trim(), form.password);
      navigate(redirectTo || ROLE_HOME[session.role] || '/', { replace: true });
    } catch (err) {
      // المرحلة 1.3: a doctor waiting for approval reaches their application —
      // the documents to send, and the decision with its reason.
      const applicant = applicantFrom(err);
      if (applicant) {
        saveApplicant(applicant);
        navigate('/apply', { replace: true });
        return;
      }
      // The backend's 403 messages are the actionable ones (suspended account,
      // pending approval), so they are shown as-is rather than generalised.
      setServerError(err.message || t('auth.invalid'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChoice = async (role) => {
    const result = await google.completeRegistration(role);
    if (result?.pending && result.applicant?.token) {
      saveApplicant(result.applicant.token);
      navigate('/apply', { replace: true });
      return;
    }
    if (result?.pending) setGoogleNotice(t('auth.doctorPending'));
  };

  return (
    <div className="auth-screen app-backdrop flex min-h-screen items-center justify-center p-4">
      <div className="auth-screen__lang">
        <LanguageSwitch />
      </div>

      <div className="w-full max-w-md">
        {/* The agency mark, above the card — or the one the Super Admin
            uploaded in its place (components/layout/BrandMarks.jsx). */}
        <div className="mb-6 flex justify-center">
          <AgencyMark variant="hero" />
        </div>

        <Panel className="auth-card border-t-4 border-t-cyan-500 shadow-2xl shadow-cyan-500/10">
          {/* The product mark, in its glowing well */}
          <div className="mb-6 flex flex-col items-center text-center">
            <ProductMark variant="card" />
            <h1 className="text-2xl font-bold text-white">{brand.ready ? (lang === 'en' ? t('auth.welcome') : welcomeLine(brand)) : ' '}</h1>
            <p className="mt-1 text-sm text-slate-400">{t('auth.tagline')}</p>
          </div>

          {google.pendingProfile ? (
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-lg font-bold text-white">{t('auth.chooseRole')}</h2>
                <p className="mt-1 text-sm text-slate-400">{google.pendingProfile.email}</p>
              </div>

              <div className="space-y-2">
                {GOOGLE_ROLES.map((role) => (
                  <button
                    key={role.value}
                    type="button"
                    disabled={google.busy}
                    onClick={() => handleRoleChoice(role.value)}
                    className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-slate-900 p-4 text-sm font-bold text-white transition-colors hover:border-cyan-500/50 hover:bg-slate-800 disabled:opacity-50"
                  >
                    {role.label}
                    <Icon name="chevron-left" size={18} className="text-slate-500 flip-ltr" />
                  </button>
                ))}
              </div>

              <Button variant="ghost" className="w-full" onClick={google.cancelRoleSelection}>
                {t('common.back')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-white">{t('auth.signInTitle')}</h2>
                {/* Which names the box takes, so nobody guesses */}
                {methods.length > 1 && (
                  <div className="auth-methods" aria-label={t('auth.methods')}>
                    {methods.map((method) => (
                      <span key={method} className="auth-methods__chip" title={t(`auth.method${method[0].toUpperCase()}${method.slice(1)}`)}>
                        <Icon name={METHOD_ICONS[method]} size={13} />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {notice && (
                <div className="flex items-start gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-300" role="status">
                  <Icon name="circle-check" size={14} className="mt-0.5 shrink-0" />
                  <span>{t(notice)}</span>
                </div>
              )}

              <Field label={copy.label} error={errors.identifier} required>
                <Input
                  value={form.identifier}
                  onChange={update('identifier')}
                  autoComplete="username"
                  placeholder={copy.placeholder}
                  autoFocus
                  dir="auto"
                  data-testid="login-identifier"
                />
              </Field>

              <Field label={t('auth.password')} error={errors.password} required>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={update('password')}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="!pe-11"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute end-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 hover:text-cyan-400"
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  >
                    <Icon name={showPassword ? 'eye-off' : 'eye'} size={17} />
                  </button>
                </div>
              </Field>

              {resetOpen && (
                <div className="-mt-1 flex justify-end">
                  <Link to="/forgot-password" state={{ identifier: form.identifier }} className="text-xs font-bold text-cyan-400 hover:text-cyan-300" data-testid="forgot-link">
                    {t('auth.forgot')}
                  </Link>
                </div>
              )}

              {serverError && (
                <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs font-bold text-red-300" role="alert">
                  <Icon name="alert-circle" size={14} className="mt-0.5 shrink-0" />
                  <span>{serverError}</span>
                </div>
              )}

              {googleNotice && (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-300">{googleNotice}</div>
              )}

              <Button type="submit" className="w-full" loading={submitting} icon="log-in" data-testid="login-submit">
                {t('auth.submit')}
              </Button>

              {consentTexts.length > 0 && (
                <p className="text-center text-[11px] leading-relaxed text-slate-400">
                  {t('auth.consentLine', {
                    terms: titleOf(consentTexts.find((x) => x.kind === 'terms') || consentTexts[0]),
                    privacy: titleOf(consentTexts.find((x) => x.kind === 'privacy') || consentTexts[consentTexts.length - 1]),
                  })}
                </p>
              )}

              {google.configured && !google.unavailable && (
                <>
                  <div className="flex items-center gap-3 py-1">
                    <span className="h-px flex-1 bg-white/10" />
                    <span className="text-[11px] text-slate-500">{t('auth.or')}</span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                  <div ref={google.buttonRef} className="flex justify-center" />
                </>
              )}

              <p className="pt-2 text-center text-xs text-slate-400">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="font-bold text-cyan-400 hover:text-cyan-300">
                  {t('auth.createAccount')}
                </Link>
              </p>
            </form>
          )}
        </Panel>

        <p className="auth-secure mt-5 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
          <Icon name="lock-keyhole" size={12} />
          {t('auth.secure')}
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
